/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { FormProvider } from 'react-hook-form';
import { StepWizardChildProps } from 'react-step-wizard';
import { UserInteractionVariant } from '@naf/user-interaction';
import { Text, TextVariant } from '@naf/text';
import { Button } from '@naf-simba/elements-buttons';
import Loader from '@components/loader';
import { Equipment } from './Equipment';
import * as S from '../../Styles';
import { NafCenter } from './NafCenter';
import { Vehicle } from './Vehicle';
import { Mileage } from './Mileage';
import { useSelectVehicleStepLogic } from './useSelectVehicleStepLogic';

interface SelectVehicleStepProps extends Partial<StepWizardChildProps> {
  nafCenterId: number;
  onUpdateProgressBarCompletedPercent: (value: number) => void;
  onUpdateProgressBarLabel: (value: string) => void;
}

export const SelectVehicleStep = ({
  nafCenterId,
  onUpdateProgressBarCompletedPercent,
  onUpdateProgressBarLabel,
  isActive,
  goToStep,
  currentStep,
}: SelectVehicleStepProps) => {
  const logic = useSelectVehicleStepLogic({
    nafCenterId,
    isActive,
    currentStep,
    goToStep,
    updateProgress: onUpdateProgressBarCompletedPercent,
    updateProgressLabel: onUpdateProgressBarLabel,
  });
  const { vehicleForm, errors, offer } = logic;

  return (
    <FormProvider {...vehicleForm}>
      {logic.isLoading || logic.shouldBeSecondStep ? (
        <S.LoaderWrapper>
          <Loader />
          {logic.isSubmitting && (
            <Text variant={TextVariant.BodyText}>Vi samler deler og priser for å kalkulere din pris</Text>
          )}
        </S.LoaderWrapper>
      ) : (
        <form onSubmit={vehicleForm.handleSubmit(logic.onSubmit)}>
          <S.StyledGrid>
            <S.StyledGridCol s="12" m="6" l="6" xl="6">
              <S.StyledInnerColContainer>
                <NafCenter
                  name="centerId"
                  control={vehicleForm.control}
                  defaultValue={nafCenterId}
                  centers={logic.centers}
                  errorMessage={errors.centerId?.message}
                  rules={{ validate: logic.validateNafCenter }}
                />
                <Vehicle
                  name="licensePlateNumber"
                  control={vehicleForm.control}
                  errorMessage={errors.licensePlateNumber?.message}
                  defaultValue={logic.vehicles.length > 0 ? logic.vehicles[0].value : ''}
                  rules={{ validate: logic.validateVehicle }}
                  vehicles={logic.vehicles}
                  handleChange={logic.handleChange}
                  vehicleLookup={logic.vehicleLookup}
                  handleLookup={logic.handleLookup}
                />
                <Mileage
                  name="mileage"
                  control={vehicleForm.control}
                  errorMessage={errors.mileage?.message}
                  rules={{
                    required: { value: true, message: 'Vi trenger kilometerstand for å kunne estimere pris.' },
                    min: { value: 1, message: 'Kilometerstand kan ikke være 0.' },
                    max: { value: 1000000, message: 'Kilometerstand kan ikke overskride 1 000 000.' },
                  }}
                />
                {offer.vehicleMaintenanceWizard?.possibleEquipment.length > 0 && (
                  <>
                    <S.StyledUserInteraction
                      className="full-width"
                      title="Velg tilgjengelig ekstrautstyr for ditt kjøretøy dersom det er relevant."
                      variant={UserInteractionVariant.Info}
                    />
                    <Equipment
                      name="equipments"
                      control={vehicleForm.control}
                      equipments={offer.vehicleMaintenanceWizard.possibleEquipment}
                      rules={{ required: false }}
                    />
                  </>
                )}
                <S.ButtonDivider />
                <S.StyledButtonsContainer className="full-width">
                  <Button type="button" variant="outlined" onClick={logic.goBack}>
                    Tilbake
                  </Button>
                  <Button type="submit" variant="outlined">
                    Neste
                  </Button>
                </S.StyledButtonsContainer>
              </S.StyledInnerColContainer>
            </S.StyledGridCol>
          </S.StyledGrid>
        </form>
      )}
    </FormProvider>
  );
};
